import {
  createDashboard,
  deleteDashboard,
  fetchDashboards,
  restoreDashboard,
} from "../api/api";
import { faPen, faTrash, faUndo } from "@fortawesome/free-solid-svg-icons";
import { useContext, useEffect, useState } from "react";

import { Button } from "@material-tailwind/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Header from "../components/layout/Header";
import { ModalContext } from "../components/context/ModalContext";
import { useAccountData } from "../components/hooks/useAccountData";
import { useNavigate } from "react-router-dom";

const DashboardCard = ({ d, onDelete, onRestore, onRename }) => {
  const navigate = useNavigate();

  return (
    <div
      className={`border rounded-lg px-5 py-4 flex items-center justify-between gap-4 pr-2 transition
        ${
          d.deleted
            ? "bg-gray-100 text-gray-400 cursor-not-allowed"
            : "bg-white hover:shadow-md hover:border-delft-blue"
        }`}
    >
      <div
        onClick={() => !d.deleted && navigate(`/${d.token}`)}
        className="flex flex-col flex-grow cursor-pointer"
      >
        <h3 className="text-md font-semibold break-all">{d.name}</h3>
        <p className="text-xs text-gray-400">
          Dummy value Created on{" "}
          {new Date(Date.now() - Math.random() * 1e10).toLocaleDateString()}
        </p>
        <p className="text-sm text-gray-500 truncate">{d.description}</p>
      </div>
      <div className="flex items-center space-x-1">
        {!d.deleted && (
          <FontAwesomeIcon
            icon={faPen}
            className="text-gray-400 hover:text-blue-500 cursor-pointer"
            onClick={() => onRename(d)}
          />
        )}
        {!d.deleted ? (
          <FontAwesomeIcon
            icon={faTrash}
            className="text-gray-400 hover:text-red-500 cursor-pointer px-5"
            onClick={() => onDelete(d.token)}
          />
        ) : (
          <FontAwesomeIcon
            icon={faUndo}
            className="text-gray-400 hover:text-green-500 cursor-pointer px-5"
            onClick={() => onRestore(d.token)}
          />
        )}
      </div>
    </div>
  );
};

const Dashboards = () => {
  // const navigate = useNavigate();
  const [dashboards, setDashboards] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [newDashboardName, setNewDashboardName] = useState("");
  const { openModal } = useContext(ModalContext);
  const account = useAccountData();
  const activeDashboards = dashboards?.filter((d) => !d.deleted) || [];
  const deletedDashboards = dashboards?.filter((d) => d.deleted) || [];

  useEffect(() => {
    fetchDashboards().then(setDashboards);
  }, []);

  const openRenameModal = (dashboard) => {
    openModal("dashboard-update", {
      name: dashboard.name,
      token: dashboard.token,
      onRename: (newName) => {
        setDashboards((prev) =>
          prev.map((dash) =>
            dash.token === dashboard.token ? { ...dash, name: newName } : dash
          )
        );
      },
    });
  };

  const handleCreate = async () => {
    if (!newDashboardName) return;
    try {
      const result = await createDashboard(newDashboardName);
      const newDashboard = { name: newDashboardName, token: result.token };
      setDashboards((prev) => [...prev, newDashboard]);
      setShowModal(false);
      setNewDashboardName("");
    } catch (error) {
      console.error(error);
    }
  };

  const handleDelete = async (token) => {
    try {
      await deleteDashboard(token);
      setDashboards((prev) =>
        prev.map((d) => (d.token === token ? { ...d, deleted: true } : d))
      );
    } catch (error) {
      console.error("Failed to delete dashboard:", error);
    }
  };

  const handleRestore = async (token) => {
    try {
      await restoreDashboard(token);
      setDashboards((prev) =>
        prev.map((d) => (d.token === token ? { ...d, deleted: false } : d))
      );
    } catch (error) {
      console.error("Failed to restore dashboard:", error);
    }
  };

  if (!account) {
    return (
      <div className="text-center py-24 text-gray-400 text-lg italic">
        Loading account data…
      </div>
    );
  }

  return (
    <div>
      <Header noconsole />
      <div className="max-w-5xl mx-auto px-6 py-12">
        <h1 className="text-4xl font-bold text-gray-800 mb-8">Dashboards</h1>

        {dashboards === null && (
          <div className="text-center py-12 text-gray-400 text-lg italic">
            Loading…
          </div>
        )}

        {dashboards?.length === 0 && (
          <div className="text-center py-12 text-gray-400 text-lg italic">
            You don’t have any dashboards yet.
            <Button
              variant="text"
              className="text-delft-blue font-semibold ml-2 underline"
              onClick={() => setShowModal(true)}
            >
              Create one
            </Button>
          </div>
        )}

        {activeDashboards.length > 0 && (
          <div className="space-y-4">
            {activeDashboards.map((d) => (
              <DashboardCard
                key={d.id}
                d={d}
                onDelete={handleDelete}
                onRestore={handleRestore}
                onRename={openRenameModal}
              />
            ))}
          </div>
        )}

        <div className="flex justify-center mt-8 pb-2">
          <Button
            className="bg-delft-blue hover:bg-delft-blue text-white font-semibold shadow transition-all duration-150"
            onClick={() => setShowModal(true)}
          >
            + New Dashboard
          </Button>
        </div>

        {deletedDashboards.length > 0 && (
          <>
            <hr className="my-6 border-gray-300" />
            <div className="space-y-4">
              {deletedDashboards.map((d) => (
                <DashboardCard
                  key={d.id}
                  d={d}
                  onDelete={handleDelete}
                  onRestore={handleRestore}
                  onRename={openRenameModal}
                />
              ))}
            </div>
          </>
        )}
      </div>
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-lg w-full max-w-md">
            <h2 className="text-xl font-semibold mb-4">Create New Dashboard</h2>
            <input
              type="text"
              value={newDashboardName}
              onChange={(e) => setNewDashboardName(e.target.value)}
              placeholder="Enter dashboard name"
              className="w-full px-3 py-2 border rounded mb-4 focus:outline-none focus:ring-2 focus:ring-delft-blue"
            />
            <div className="flex justify-end space-x-2">
              <Button
                className="bg-gray-300 text-gray-800"
                onClick={() => setShowModal(false)}
              >
                Cancel
              </Button>
              <Button
                className="bg-delft-blue text-white"
                onClick={handleCreate}
              >
                Create
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboards;
